// property.js — Property page specific code
// Loaded only on the property detail page.
// Handles: gallery, reviews panel, room modal, map, save/share, reserve buttons.

// Currency decimal tables. Kept in sync with internal/models/currency.go.
// Zero-decimal currencies render without cents; three-decimal currencies
// preserve sub-cent precision for dinar-family codes.
const CURRENCY_DECIMALS_ZERO = new Set(['JPY','KRW','HUF','CLP','IDR','ISK','VND','XOF','XAF','XPF','RWF','UGX','VUV','PYG','MGA']);
const CURRENCY_DECIMALS_THREE = new Set(['BHD','JOD','KWD','OMR','TND']);

function currencyDecimals(code) {
    if (!code) return 2;
    const c = String(code).toUpperCase();
    if (CURRENCY_DECIMALS_ZERO.has(c)) return 0;
    if (CURRENCY_DECIMALS_THREE.has(c)) return 3;
    return 2;
}

// fmtAmountDisplay mirrors the Go template fmtAmount: thousand separators,
// currency-aware decimals. For user-visible text only.
function fmtAmountDisplay(amount, code) {
    const d = currencyDecimals(code);
    return (Number(amount) || 0).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });
}

// fmtAmountRaw mirrors the Go template fmtAmountRaw: no separators, currency
// decimals. For URL/form transport.
function fmtAmountRaw(amount, code) {
    return (Number(amount) || 0).toFixed(currencyDecimals(code));
}

// ============================================
// SHARE TOOLTIP HELPER
// ============================================
// Save logic now lives in <save-button> (static/js/components/save-button.js).
// The share button still uses showTooltip for "Link copied!" feedback.

function showTooltip(id, text) {
    const el = document.getElementById(id);
    if (!el) return;
    if (text) el.textContent = text;
    el.classList.remove('opacity-0');
    el.classList.add('opacity-100');
    setTimeout(() => {
        el.classList.remove('opacity-100');
        el.classList.add('opacity-0');
    }, 1500);
}

// ============================================
// SHARE PROPERTY
// ============================================

function shareProperty() {
    const url = window.location.href;
    const title = window.propertyName || document.title;

    if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(() => {});
        return;
    }

    navigator.clipboard.writeText(url).then(() => {
        showTooltip('share-tooltip', 'Link copied!');
    }).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showTooltip('share-tooltip', 'Link copied!');
    });
}

// ============================================
// RESERVE BUTTONS
// ============================================

// Reserve buttons live in the SSR shell (top + sidebar) and stay
// stable across the <room-availability> innerHTML swap. Bind once at
// DOMContentLoaded via document-level event delegation so the
// listeners survive the swap and any future re-renders.
//
// Race fix (room_availability_decouple_plan.md v3 §3, H-v1-4): SSR
// emits `data-needs-rooms` on the buttons when dates are present but
// rooms haven't fetched yet. The click handler early-returns while
// the attribute is set; the rooms-loaded listener clears it on every
// fetch outcome (live, cached-fallback, error). After clearing,
// handleDirectBooking collects .room-select; if none, calls
// navigateToRequestCheckout. For the no-dates path, the attribute is
// never set (PR-cf.cache.collapse: Phase B in property.html only adds
// data-needs-rooms when window.hasDates is true), so the click goes
// straight to handleDirectBooking which nudges the date picker.
function initReserveButtonDelegation() {
    document.addEventListener('click', function (e) {
        const btn = e.target.closest('[data-reserve-btn]');
        if (!btn) return;
        if (btn.hasAttribute('data-needs-rooms')) {
            // PR-perf.23: race-gate is behavior-only now (the yellow CSS
            // variant was removed because the yellow→blue flicker on
            // rooms-loaded was jarring). Click while data-needs-rooms is
            // present still early-returns from the checkout submit path,
            // but we route the visitor to the #rooms section so the
            // click isn't a silent no-op. flashAttention pulses the
            // section header to draw the eye.
            e.preventDefault();
            const rooms = document.getElementById('rooms');
            if (rooms) {
                flashAttention(rooms, { focusSelector: 'h2' });
            }
            return;
        }
        handleReserveClick(e, btn);
    });
}

// Room-select change handlers re-bind on every `rooms-loaded` event
// because the <room-availability> innerHTML swap replaces the
// .room-select nodes. Idempotent: a fresh node has no listener, so
// repeat firings (e.g. after admin Refresh round-trip) are safe.
function initRoomSelectListeners() {
    document.querySelectorAll('.room-select').forEach(select => {
        select.addEventListener('change', updateReserveButton);
    });
    // Recompute the reserve summary after a swap — the previous
    // selection state is gone, but the summary chrome (top banner,
    // sidebar) is in the SSR shell and needs to be reset.
    if (typeof updateReserveButton === 'function') updateReserveButton();
}

// rooms-loaded fires from <room-availability> after its fetch
// resolves (live, cached-fallback, or error). Always clear
// data-needs-rooms so the top/sidebar Reserve buttons become
// clickable; handleReserveClick → handleDirectBooking auto-routes:
//   - .room-select nodes present (live rooms) → collect + checkout
//   - no .room-select (cached fallback / error template) →
//     navigateToRequestCheckout
//   - hasDates=false → flashAttention(date-picker)
// detail.hasLiveRooms is kept on the event for any future listeners
// that want to differentiate, but the gate itself is binary
// (loaded-or-not).
document.addEventListener('rooms-loaded', function () {
    initRoomSelectListeners();
    document.querySelectorAll('[data-reserve-btn][data-needs-rooms]').forEach(function (btn) {
        btn.removeAttribute('data-needs-rooms');
    });
});

function handleReserveClick(e, _btn) {
    e.preventDefault();
    handleDirectBooking();
}

// --- Direct Booking Flow ---

function collectSelectedRooms() {
    var rooms = [];
    document.querySelectorAll('.room-select').forEach(function(sel) {
        var qty = parseInt(sel.value) || 0;
        if (qty > 0) {
            rooms.push({
                blockId: sel.dataset.blockId || '',
                name: sel.dataset.roomName || 'Room',
                qty: qty,
                unitPrice: parseFloat(sel.dataset.unitPrice) || 0,
                taxes: parseFloat(sel.dataset.taxes) || 0,
                currency: sel.dataset.currency || 'USD',
                currencySymbol: sel.dataset.currencySymbol || '$',
                cancellationText: sel.dataset.cancellationText || '',
                prepaymentText: sel.dataset.prepaymentText || '',
                mealplanText: sel.dataset.mealplanText || ''
            });
        }
    });
    return rooms;
}

// flashAttention scrolls a target into view, focuses an inner element if one
// matches focusSelector (so screen readers announce the section), and briefly
// pulses a soft ring around it via the .flash-pulse CSS class (see styles.css).
// Falls back to a blocking alert() if the target can't be located, so the user
// always gets some signal. Match for booking.com's "Reserve without selection"
// behavior — soft visual nudge, no modal interruption.
function flashAttention(target, opts) {
    opts = opts || {};
    if (!target) {
        if (opts.fallbackMessage) alert(opts.fallbackMessage);
        return;
    }
    try {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        var focusEl = opts.focusSelector ? target.querySelector(opts.focusSelector) : null;
        if (focusEl) {
            // Make headings focusable without changing their tab order.
            if (!focusEl.hasAttribute('tabindex')) focusEl.setAttribute('tabindex', '-1');
            // Defer focus until after smooth-scroll starts so iOS doesn't snap.
            setTimeout(function () { try { focusEl.focus({ preventScroll: true }); } catch (e) {} }, 200);
        }
        var pulseEl = (opts.pulseSelector ? target.querySelector(opts.pulseSelector) : null) || target;
        // Restart the animation if it's already running by removing then re-adding.
        pulseEl.classList.remove('flash-pulse');
        // Force reflow so the re-added class restarts the keyframes.
        void pulseEl.offsetWidth;
        pulseEl.classList.add('flash-pulse');
        // Auto-clean after the animation finishes (3 × 0.6s = 1.8s + buffer).
        setTimeout(function () { pulseEl.classList.remove('flash-pulse'); }, 2200);
    } catch (e) {
        if (opts.fallbackMessage) alert(opts.fallbackMessage);
    }
}

function handleDirectBooking() {
    // Must have dates — nudge the user toward the date picker rather than alerting.
    if (!window.hasDates) {
        flashAttention(
            document.querySelector('date-range-picker') || document.querySelector('input[name="checkin"]'),
            { fallbackMessage: 'Please select dates to continue booking.' }
        );
        return;
    }

    var rooms = collectSelectedRooms();
    if (rooms.length === 0) {
        // No room selectors on page = no-rooms scenario (e.g. request booking) — go to request checkout
        if (document.querySelectorAll('.room-select').length === 0) {
            navigateToRequestCheckout();
            return;
        }
        // Nudge toward the rooms section, focus the heading for a11y, pulse it.
        flashAttention(document.getElementById('rooms'), {
            focusSelector: 'h2',
            fallbackMessage: 'Please select at least one room.',
        });
        return;
    }

    // Calculate totals for display (server will resolve authoritative prices)
    var totalPrice = 0, totalTaxes = 0, totalQty = 0;
    var currency = rooms[0].currency;
    var sym = rooms[0].currencySymbol;
    rooms.forEach(function(r) {
        totalPrice += r.unitPrice * r.qty;
        totalTaxes += r.taxes * r.qty;
        totalQty += r.qty;
    });
    var totalAmount = totalPrice + totalTaxes;

    var params = new URLSearchParams(window.location.search);
    var checkin = params.get('checkin') || '';
    var checkout = params.get('checkout') || '';

    // Server-side booking data: only block_ids + currency (server resolves prices)
    var bookingData = {
        property_id: window.propertyID || '',
        rooms: rooms.map(function(r) { return { block_id: r.blockId, qty: r.qty }; }),
        currency: currency,
        checkin: checkin,
        checkout: checkout,
        adults: window.searchAdults || 2,
        children: window.searchChildren || 0
    };

    navigateToCheckout(bookingData, rooms, sym, totalPrice, totalTaxes, totalQty);
}

function navigateToCheckout(data, rooms, sym, totalPrice, totalTaxes, totalQty) {
    var params = new URLSearchParams();
    params.set('property_id', data.property_id);
    params.set('block_ids', data.rooms.map(function(r) { return r.block_id + ':' + r.qty; }).join(','));
    params.set('currency', data.currency);
    params.set('checkin', data.checkin);
    params.set('checkout', data.checkout);
    params.set('adults', data.adults);
    params.set('children', data.children);
    // Carry children_ages through to /checkout → /api/bookings so the
    // room availability cache lookup at submit-time matches the row
    // populated by the property page render. Without this, every
    // family booking misses the cache and falls to the fallback
    // request path. Audit (PR3 Plan agent round 1) HIGH.
    var srcParams = new URLSearchParams(window.location.search);
    var ages = srcParams.getAll('age').filter(function(a) { return a !== ''; });
    if (ages.length > 0) {
        params.set('children_ages', ages.join(','));
    }
    // Display params (for checkout sidebar — NOT used for booking validation)
    params.set('room_name', rooms.map(function(r) { return r.qty > 1 ? r.name + ' x' + r.qty : r.name; }).join(', '));
    params.set('rooms_count', totalQty);
    params.set('total_price', fmtAmountRaw(totalPrice, data.currency));
    params.set('taxes', fmtAmountRaw(totalTaxes, data.currency));
    params.set('currency_symbol', sym);
    params.set('property_name', window.propertyName || '');
    params.set('property_image', window.propertyImage || '');
    params.set('property_address', window.propertyAddress || '');
    // Policy texts from first selected room (display only — server resolves at booking time)
    var firstRoom = rooms[0] || {};
    if (firstRoom.cancellationText) params.set('cancellation_policy', firstRoom.cancellationText);
    if (firstRoom.prepaymentText) params.set('prepayment_policy', firstRoom.prepaymentText);
    if (firstRoom.mealplanText) params.set('meal_policy', firstRoom.mealplanText);
    window.location.href = '/checkout?' + params.toString();
}

function navigateToRequestCheckout() {
    var params = new URLSearchParams(window.location.search);
    var urlParams = new URLSearchParams();
    urlParams.set('property_id', window.propertyID || '');
    urlParams.set('checkin', params.get('checkin') || '');
    urlParams.set('checkout', params.get('checkout') || '');
    urlParams.set('adults', params.get('adults') || '2');
    urlParams.set('children', params.get('children') || '0');
    urlParams.set('property_name', window.propertyName || '');
    urlParams.set('property_image', window.propertyImage || '');
    urlParams.set('property_address', window.propertyAddress || '');
    urlParams.set('mode', 'request');
    urlParams.set('rooms_count', '1');

    // Pricing path: roomGroupsData is canonical USD (property page is CDN-cached
    // canonical). Convert amount + taxes to user's preferred currency BEFORE
    // building the URL so total_price, taxes, currency, and currency_symbol
    // are all internally consistent — no $-amount + EUR-currency mismatch.
    // Async: rates may need a network fetch. If conversion fails (rates 5xx
    // or missing target rate), the convert API returns source-currency so
    // the URL ends up consistently USD across all four params. Documented
    // degraded fallback per memory/pre_cloudflare_fixes_plan.md §3.2.
    var group = (window.roomGroupsData && window.roomGroupsData.length > 0) ? window.roomGroupsData[0] : null;
    if (group) {
        urlParams.set('room_name', group.name || 'Room');
    }
    var rate = (group && group.rates && group.rates.length > 0) ? group.rates[0] : null;
    var srcAmount = rate ? ((rate.price || {}).amount || 0) : 0;
    var srcTaxes = rate ? ((rate.price || {}).taxes_and_fees || 0) : 0;
    var srcCurrency = (rate && rate.price && rate.price.currency) || 'USD';

    var converter = (window.PriceDisplay && window.PriceDisplay.convert)
        ? window.PriceDisplay.convert
        : function (a, c) { return Promise.resolve({ amount: a, currency: c }); };

    Promise.all([
        converter(parseFloat(srcAmount) || 0, srcCurrency),
        converter(parseFloat(srcTaxes) || 0, srcCurrency),
    ]).then(function (results) {
        var amountResult = results[0];
        var taxesResult = results[1];
        // Both calls hit the same rates cache, so their `currency` agrees.
        var displayCurrency = amountResult.currency;
        var symbols = window.currencySymbols || {};
        var symbol = symbols[displayCurrency] || (displayCurrency + ' ');
        urlParams.set('total_price', String(amountResult.amount));
        urlParams.set('taxes', String(taxesResult.amount));
        urlParams.set('currency', displayCurrency);
        urlParams.set('currency_symbol', symbol);
        window.location.href = '/checkout?' + urlParams.toString();
    }).catch(function () {
        // Truly catastrophic — should not happen since convert never rejects,
        // but defense: fall back to source values.
        urlParams.set('total_price', String(srcAmount));
        urlParams.set('taxes', String(srcTaxes));
        urlParams.set('currency', srcCurrency);
        urlParams.set('currency_symbol', (window.currencySymbols || {})[srcCurrency] || '$');
        window.location.href = '/checkout?' + urlParams.toString();
    });
}

function updateReserveButton() {
    const reserveBtn = document.querySelector('[data-reserve-btn]');
    if (!reserveBtn) return;

    let totalRooms = 0;
    let totalPrice = 0;
    let sourceCurrency = '';

    // data-unit-price is in the SOURCE currency that SSR rendered (the
    // user's then-display currency, or post-Phase-3 USD). After a switch
    // the user's preferred display currency may differ — we round-trip
    // through window.PriceDisplay.format which resolves to the user's
    // current pref via /api/rates.
    document.querySelectorAll('.room-select').forEach(select => {
        const qty = parseInt(select.value) || 0;
        if (qty > 0) {
            totalRooms += qty;
            const price = parseFloat(select.dataset.unitPrice) || 0;
            totalPrice += price * qty;
            if (select.dataset.currency) sourceCurrency = select.dataset.currency;
        }
    });

    const summary = document.getElementById('selection-summary');
    const reserveSummaries = document.querySelectorAll('.reserve-summary');

    const applyDisplay = function (text) {
        if (summary) {
            if (totalRooms > 0) {
                summary.classList.remove('hidden');
                document.getElementById('summary-rooms').textContent = totalRooms;
                document.getElementById('summary-price').textContent = text;
            } else {
                summary.classList.add('hidden');
            }
        }
        const summaryText = totalRooms > 0
            ? totalRooms + (totalRooms === 1 ? ' room' : ' rooms') + ' for ' + text
            : '';
        reserveSummaries.forEach(function (el) {
            if (totalRooms > 0) {
                el.textContent = summaryText;
                el.classList.remove('hidden');
                el.parentElement.classList.add('flex', 'flex-col');
            } else {
                el.textContent = '';
                el.classList.add('hidden');
                el.parentElement.classList.remove('flex', 'flex-col');
            }
        });
    };

    if (totalRooms > 0 && window.PriceDisplay && sourceCurrency) {
        window.PriceDisplay.format(totalPrice, sourceCurrency).then(function (r) {
            applyDisplay(r.text);
        });
    } else {
        applyDisplay('');
    }

    // The button stays visually consistent regardless of qty: stale
    // opacity-50/cursor-not-allowed treatment made the qty=0 state look
    // unclickable AND failed Lighthouse color-contrast (white-on-50%-blue
    // = 2.4:1). Pre-load "not ready" is signalled via the data-needs-rooms
    // attribute + bg-booking-yellow CSS variant; pre-selection (qty=0)
    // doesn't need a visual disabled state — the room selector below is
    // the obvious next step and href="#rooms" scrolls the visitor there
    // if they click prematurely. PR-a11y.5 follow-up to PR-a11y.4.
    if (totalRooms > 0) {
        reserveBtn.classList.add('hover:bg-booking-blue-dark');
    } else {
        reserveBtn.classList.remove('hover:bg-booking-blue-dark');
    }
}

// Re-render the reserve button + summary when the user switches currency
// via the header dropdown. <currency-switcher> dispatches 'currency-change'
// on document; SSR data-unit-price stays in the original currency, so
// updateReserveButton + window.PriceDisplay.format together produce the
// converted text.
document.addEventListener('currency-change', function () {
    if (typeof updateReserveButton === 'function') updateReserveButton();
});

// ============================================
// MAP INTEGRATION (Leaflet.js)
// ============================================

let map = null;

function initMap() {
    const mapContainer = document.getElementById('property-mini-map');
    if (!mapContainer) return;
    // PR-perf.15: idempotency guard — initMapLazy may fire IO twice in
    // edge cases (rapid scroll-up-then-down before disconnect), and the
    // openMapModal click path also triggers loadLeaflet. Without this
    // guard, L.map() on an already-initialized container throws
    // "Map container is already initialized."
    if (mapContainer.dataset.mapInitialized === '1') return;

    const lat = parseFloat(mapContainer.dataset.lat);
    const lng = parseFloat(mapContainer.dataset.lng);
    const propertyName = mapContainer.dataset.name || 'Property';

    if (isNaN(lat) || isNaN(lng)) return;
    // PR-perf.3: lazy-load Leaflet on first use instead of from base.html.
    if (typeof L === 'undefined') {
        loadLeaflet(initMap);
        return;
    }
    mapContainer.dataset.mapInitialized = '1';

    mapContainer.parentElement.style.position = 'relative';
    mapContainer.parentElement.style.zIndex = '1';

    const customIcon = L.divIcon({
        className: 'custom-map-marker',
        html: `<div style="width:32px;height:32px;background:#003580;border:3px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 5px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
            <svg style="transform:rotate(45deg);width:14px;height:14px;" fill="white" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
    });

    // divIcon markers render as <div role="button" tabindex="0">; Leaflet's
    // `alt` option only applies to <img> markers, so set aria-label directly
    // on the DOM node post-add. Lighthouse aria-command-name otherwise fails.
    const marker = L.marker([lat, lng], { icon: customIcon });
    L.map(mapContainer, {
        zoomControl: false, dragging: false, touchZoom: false,
        scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false,
        keyboard: false, attributionControl: false
    }).setView([lat, lng], 15).addLayer(
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
    ).addLayer(marker);
    const markerEl = marker.getElement();
    if (markerEl) markerEl.setAttribute('aria-label', propertyName + ' location marker');
}

// PR-perf.15: don't load Leaflet at DCL. Wait until the map element
// scrolls within viewport, then load + init. Until the IO fires, the
// page costs zero Leaflet bytes — saves ~28 KiB JS + 4 KiB CSS + the
// openstreetmap.org tile fetches that otherwise contend with the LCP
// image. Idempotent via initMap's dataset.mapInitialized guard.
function initMapLazy() {
    const mapContainer = document.getElementById('property-mini-map');
    if (!mapContainer) return;
    if (mapContainer.dataset.mapInitialized === '1') return;
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting) {
                io.disconnect();
                initMap();
            }
        }, { rootMargin: '200px' });
        io.observe(mapContainer);
    } else {
        // Browsers without IO: defer to idle.
        if ('requestIdleCallback' in window) {
            requestIdleCallback(initMap, { timeout: 3000 });
        } else {
            setTimeout(initMap, 1500);
        }
    }
}

function openMapModal(lat, lng, propertyName) {
    const modal = document.createElement('div');
    modal.id = 'map-modal';
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4';
    modal.style.zIndex = '9999';
    modal.innerHTML = `
        <div class="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div class="flex items-center justify-between p-4 border-b">
                <h3 class="font-bold text-lg">${escapeHTML(propertyName)}</h3>
                <button onclick="closeMapModal()" class="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            <div id="modal-map" class="w-full h-[60vh]"></div>
        </div>`;
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    if (typeof L === 'undefined') {
        loadLeaflet(() => initModalMap(lat, lng, propertyName));
    } else {
        initModalMap(lat, lng, propertyName);
    }

    modal.addEventListener('click', (e) => { if (e.target === modal) closeMapModal(); });
    document.addEventListener('keydown', mapModalKeyHandler);
}

function initModalMap(lat, lng, propertyName) {
    const modalMap = L.map('modal-map').setView([lat, lng], 15);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(modalMap);
    L.marker([lat, lng]).addTo(modalMap).bindPopup(`<strong>${escapeHTML(propertyName)}</strong>`).openPopup();
    setTimeout(() => modalMap.invalidateSize(), 100);
}

function closeMapModal() {
    const modal = document.getElementById('map-modal');
    if (modal) { modal.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', mapModalKeyHandler); }
}

function mapModalKeyHandler(e) { if (e.key === 'Escape') closeMapModal(); }

// Idempotent loader — guarded against double-load (e.g. bfcache restore,
// concurrent initMap + openMapModal). Once Leaflet's `L` is in scope OR a
// load is in flight, subsequent calls enqueue their callback for the same
// onload event instead of injecting another <script>.
let _leafletLoadCbs = null;
function loadLeaflet(callback) {
    if (typeof L !== 'undefined') { callback && callback(); return; }
    if (_leafletLoadCbs) { _leafletLoadCbs.push(callback); return; }
    _leafletLoadCbs = [callback];
    const css = document.createElement('link');
    css.rel = 'stylesheet'; css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = function () {
        const cbs = _leafletLoadCbs || [];
        _leafletLoadCbs = null;
        for (let i = 0; i < cbs.length; i++) { try { cbs[i] && cbs[i](); } catch (e) {} }
    };
    document.head.appendChild(script);
}

function showOnMap(lat, lng, name) { openMapModal(lat, lng, name); }

// ============================================
// PHOTO GALLERY - Full Page View
// ============================================

function initGallery() {
    document.querySelectorAll('.gallery-image').forEach((img) => {
        img.addEventListener('click', (e) => { e.stopPropagation(); openGalleryView(); });
    });
}

function openGalleryView(e) {
    if (e && e.stopPropagation) e.stopPropagation();

    const mainImage = window.galleryMainImage || '';
    const galleryImages = window.galleryImages || [];
    const allImages = mainImage
        ? [mainImage, ...galleryImages.filter(img => img !== mainImage)]
        : [...galleryImages];

    if (allImages.length === 0) return;

    const reviews = window.galleryReviews || [];
    const reviewScore = window.galleryReviewScore || 0;
    const reviewScoreText = window.galleryReviewScoreText || '';
    const reviewCount = window.galleryReviewCount || 0;
    const propertyName = window.propertyName || '';

    const gallery = document.createElement('div');
    gallery.id = 'gallery-fullpage';
    gallery.className = 'fixed inset-0 z-50 bg-white flex flex-col';
    gallery.style.animation = 'fadeIn 0.2s ease-in';

    let reviewsSidebar = '';
    if (reviewScore > 0) {
        const reviewCards = reviews.filter(r => r.positive).map(r => `
            <div class="border rounded-lg p-3">
                <p class="text-sm text-gray-700 italic" style="display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;">"${escapeHTML(r.positive.substring(0, 200))}"</p>
                <div class="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <div class="w-6 h-6 bg-booking-blue text-white rounded-full flex items-center justify-center text-xs font-bold">
                        ${escapeHTML((r.guest_name || '?').charAt(0).toUpperCase())}
                    </div>
                    <span class="font-medium text-gray-700">${escapeHTML(r.guest_name || '')}</span>
                    <span>${formatCountry(r)}</span>
                </div>
            </div>
        `).join('');

        reviewsSidebar = `
            <div class="hidden lg:flex flex-col w-80 border-l shrink-0 overflow-y-auto">
                <div class="p-4 border-b bg-white sticky top-0 z-10">
                    <div class="flex items-center gap-3">
                        <div class="w-12 h-12 bg-booking-blue text-white rounded-tr-lg rounded-br-lg rounded-bl-lg flex items-center justify-center text-lg font-bold shrink-0">
                            ${parseFloat(reviewScore).toFixed(1)}
                        </div>
                        <div>
                            <p class="font-bold">${escapeHTML(reviewScoreText)}</p>
                            <p class="text-sm text-gray-500">${reviewCount} reviews</p>
                        </div>
                    </div>
                </div>
                <div class="p-4 space-y-4">
                    <p class="text-sm font-semibold text-gray-700">See what guests loved the most:</p>
                    ${reviewCards}
                </div>
            </div>`;
    }

    const photoGrid = allImages.map((src, i) => `
        <div class="${i === 0 ? 'sm:col-span-2 sm:row-span-2' : ''}">
            <img src="${src}" alt="Photo ${i + 1}"
                 class="w-full h-full object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                 style="min-height:${i === 0 ? '300px' : '150px'};max-height:${i === 0 ? '500px' : '250px'};"
                 onclick="openGalleryLightbox(${i})"
                 ${i >= 10 ? 'loading="lazy"' : ''}>
        </div>
    `).join('');

    gallery.innerHTML = `
        <div class="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
            <div class="flex items-center gap-3 min-w-0">
                <h2 class="text-lg font-bold truncate">${escapeHTML(propertyName)}</h2>
            </div>
            <div class="flex items-center gap-3 shrink-0">
                <button onclick="closeGalleryView(); var el = document.getElementById('rooms'); if(el) el.scrollIntoView({behavior:'smooth', block:'start'});"
                   class="hidden sm:inline-flex px-4 py-2 bg-booking-blue text-white rounded text-sm font-medium hover:bg-booking-blue-dark">
                    Reserve now
                </button>
                <span class="text-sm text-gray-500">${allImages.length} photos</span>
                <button onclick="closeGalleryView()" class="flex items-center gap-1 p-2 hover:bg-gray-100 rounded-full text-gray-600" aria-label="Close gallery">
                    Close
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
        </div>
        <div class="flex-1 overflow-hidden flex">
            <div id="gallery-photo-grid" class="flex-1 overflow-y-auto p-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                    ${photoGrid}
                </div>
            </div>
            ${reviewsSidebar}
        </div>`;

    gallery.dataset.images = JSON.stringify(allImages);
    document.body.appendChild(gallery);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', galleryKeyHandler);
}

function closeGalleryView() {
    closeGalleryLightbox();
    const gallery = document.getElementById('gallery-fullpage');
    if (gallery) {
        gallery.remove();
        document.body.style.overflow = '';
        document.removeEventListener('keydown', galleryKeyHandler);
    }
}

function openGalleryLightbox(index) {
    closeGalleryLightbox();
    const gallery = document.getElementById('gallery-fullpage');
    if (!gallery) return;

    const images = JSON.parse(gallery.dataset.images || '[]');
    if (images.length === 0) return;

    const lightbox = document.createElement('div');
    lightbox.id = 'gallery-lightbox';
    lightbox.className = 'fixed inset-0 z-[60] bg-black/95 flex items-center justify-center';
    lightbox.dataset.images = JSON.stringify(images);
    lightbox.dataset.currentIndex = index;

    lightbox.innerHTML = `
        <button onclick="closeGalleryLightbox()" class="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-10 p-2">&times;</button>
        <button onclick="galleryLightboxNav(-1)" class="absolute left-4 text-white text-5xl hover:text-gray-300 p-4 z-10">&lsaquo;</button>
        <img src="${images[index]}" class="max-h-[90vh] max-w-[90vw] object-contain" id="gallery-lightbox-image">
        <button onclick="galleryLightboxNav(1)" class="absolute right-4 text-white text-5xl hover:text-gray-300 p-4 z-10">&rsaquo;</button>
        <div class="absolute bottom-4 text-white text-center w-full">
            <span id="gallery-lightbox-counter">${index + 1} / ${images.length}</span>
        </div>`;

    lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeGalleryLightbox(); });
    document.body.appendChild(lightbox);
}

function closeGalleryLightbox() {
    const lb = document.getElementById('gallery-lightbox');
    if (lb) lb.remove();
}

function galleryLightboxNav(delta) {
    const lb = document.getElementById('gallery-lightbox');
    if (!lb) return;
    const images = JSON.parse(lb.dataset.images);
    let index = parseInt(lb.dataset.currentIndex) + delta;
    if (index < 0) index = images.length - 1;
    if (index >= images.length) index = 0;
    lb.dataset.currentIndex = index;
    document.getElementById('gallery-lightbox-image').src = images[index];
    document.getElementById('gallery-lightbox-counter').textContent = `${index + 1} / ${images.length}`;
}

function galleryKeyHandler(e) {
    const lb = document.getElementById('gallery-lightbox');
    if (e.key === 'Escape') {
        if (lb) closeGalleryLightbox();
        else closeGalleryView();
    }
    if (e.key === 'ArrowLeft' && lb) galleryLightboxNav(-1);
    if (e.key === 'ArrowRight' && lb) galleryLightboxNav(1);
}

// ============================================
// REVIEWS
// ============================================

async function loadMoreReviews(propertyId, page) {
    const container = document.getElementById('panel-reviews-list');
    const loadMoreBtn = document.getElementById('load-more-reviews');

    if (!container || !loadMoreBtn) return;

    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = 'Loading...';

    try {
        const lang = document.getElementById('reviews-filter-language')?.value || '';
        const langParam = lang ? `&language=${lang}` : '';
        const response = await fetch(`/api/reviews?property_id=${propertyId}&page=${page}&per_page=10${langParam}`);
        const data = await response.json();

        if (data.reviews && data.reviews.length > 0) {
            data.reviews.forEach(review => {
                container.insertAdjacentHTML('beforeend', createReviewHTML(review));
            });

            loadMoreBtn.dataset.page = page + 1;
            loadMoreBtn.disabled = false;
            loadMoreBtn.textContent = 'Show more reviews';

            if (!data.has_more) loadMoreBtn.style.display = 'none';
        } else {
            loadMoreBtn.style.display = 'none';
        }
    } catch (error) {
        console.error('Error loading reviews:', error);
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Try again';
    }
}

async function reloadReviewsByLanguage() {
    const container = document.getElementById('panel-reviews-list');
    const loadMoreBtn = document.getElementById('load-more-reviews');
    if (!container) return;

    const lang = document.getElementById('reviews-filter-language')?.value || '';
    const propertyId = loadMoreBtn?.dataset.propertyId;
    if (!propertyId) return;

    container.innerHTML = '<div class="text-center py-8 text-gray-500">Loading reviews...</div>';

    try {
        const langParam = lang ? `&language=${lang}` : '';
        const response = await fetch(`/api/reviews?property_id=${propertyId}&page=1&per_page=10${langParam}`);
        const data = await response.json();

        container.innerHTML = '';

        if (data.reviews && data.reviews.length > 0) {
            data.reviews.forEach(review => {
                container.insertAdjacentHTML('beforeend', createReviewHTML(review));
            });

            if (loadMoreBtn) {
                loadMoreBtn.dataset.page = 2;
                loadMoreBtn.disabled = false;
                loadMoreBtn.textContent = 'Show more reviews';
                loadMoreBtn.style.display = data.has_more ? '' : 'none';
            }
        } else {
            container.innerHTML = '<div class="text-center py-8 text-gray-500">No reviews found for this language.</div>';
            if (loadMoreBtn) loadMoreBtn.style.display = 'none';
        }
    } catch (error) {
        console.error('Error loading reviews:', error);
        container.innerHTML = '<div class="text-center py-8 text-red-500">Failed to load reviews.</div>';
    }
}

function createReviewHTML(review) {
    const initial = review.guest_name ? review.guest_name.charAt(0) : '?';
    const score = typeof review.score === 'number' ? review.score.toFixed(1) : review.score;
    const date = review.date ? formatReviewDate(review.date) : '';
    return `
        <div class="border rounded-lg p-4 review-card"
             data-trip-type="${review.trip_type || ''}"
             data-score="${review.score || ''}"
             data-date="${review.date || ''}"
             data-helpful="${review.helpful_vote_count || 0}">
            <div class="flex items-start gap-3">
                <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-booking-blue text-white rounded-full flex items-center justify-center font-semibold">${initial}</div>
                </div>
                <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-2 mb-1">
                        <div>
                            <p class="font-semibold">${review.guest_name}</p>
                            <p class="text-xs text-gray-500">${formatCountry(review)}</p>
                        </div>
                        <div class="flex-shrink-0 w-10 h-10 bg-booking-blue text-white rounded flex items-center justify-center font-bold">${score}</div>
                    </div>
                    <div class="text-xs text-gray-500 mb-2 flex flex-wrap items-center gap-2">
                        ${review.room_photo_url ? `<img src="${review.room_photo_url}" alt="${review.room_type || ''}" class="w-14 h-10 object-cover rounded border flex-shrink-0">` : ''}
                        ${review.room_type ? `<span>${review.room_type}</span>` : ''}
                        ${date ? `<span>${date}</span>` : ''}
                        ${review.trip_type ? `<span>${review.trip_type}</span>` : ''}
                    </div>
                    ${review.positive ? `<div class="mb-2 flex gap-2"><span class="text-green-600 font-bold flex-shrink-0">+</span><span class="text-gray-700 text-sm">${review.positive}</span></div>` : ''}
                    ${review.negative ? `<div class="flex gap-2"><span class="text-red-600 font-bold flex-shrink-0">\u2212</span><span class="text-gray-700 text-sm">${review.negative}</span></div>` : ''}
                    ${(review.helpful_vote_count || 0) > 0 ? `<div class="mt-2 text-xs text-gray-400 flex items-center gap-1"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"/></svg>${review.helpful_vote_count} found this helpful</div>` : ''}
                </div>
            </div>
        </div>`;
}

// ============================================
// REVIEWS SLIDE-IN PANEL
// ============================================

function openReviewsPanel() {
    const panel = document.getElementById('reviews-panel');
    const overlay = document.getElementById('reviews-overlay');
    if (panel && overlay) {
        overlay.classList.remove('hidden');
        void overlay.offsetWidth;
        overlay.classList.add('open');
        panel.classList.add('open');
        document.body.classList.add('reviews-panel-open');
    }
}

function closeReviewsPanel() {
    const panel = document.getElementById('reviews-panel');
    const overlay = document.getElementById('reviews-overlay');
    if (panel && overlay) {
        panel.classList.remove('open');
        overlay.classList.remove('open');
        setTimeout(() => {
            overlay.classList.add('hidden');
            document.body.classList.remove('reviews-panel-open');
        }, 300);
    }
}

function getActiveTopics() {
    const active = [];
    document.querySelectorAll('.topic-filter.active').forEach(btn => active.push(btn.dataset.topic));
    return active;
}

function reviewMatchesTopics(card, topics) {
    if (topics.length === 0) return true;
    const topicKeywords = window.topicKeywords || {};
    const text = (card.textContent || '').toLowerCase();
    return topics.every(topic => {
        const keywords = topicKeywords[topic] || [];
        return keywords.some(kw => text.includes(kw));
    });
}

function filterReviews() {
    const travelerFilter = document.getElementById('reviews-filter-traveler')?.value || '';
    const scoreFilter = parseFloat(document.getElementById('reviews-filter-score')?.value) || 0;
    const activeTopics = getActiveTopics();
    const reviewCards = document.querySelectorAll('#panel-reviews-list .review-card');
    const noReviewsMessage = document.getElementById('no-reviews-message');

    let visibleCount = 0;
    reviewCards.forEach(card => {
        const tripType = card.dataset.tripType || '';
        const score = parseFloat(card.dataset.score) || 0;
        const matchesTraveler = !travelerFilter || tripType === travelerFilter;
        const matchesScore = !scoreFilter || score >= scoreFilter;
        const matchesTopics = reviewMatchesTopics(card, activeTopics);
        if (matchesTraveler && matchesScore && matchesTopics) {
            card.style.display = '';
            visibleCount++;
        } else {
            card.style.display = 'none';
        }
    });

    if (noReviewsMessage) {
        noReviewsMessage.classList[visibleCount === 0 ? 'remove' : 'add']('hidden');
    }
}

function sortReviews() {
    const sortValue = document.getElementById('reviews-sort')?.value || 'recent';
    const container = document.getElementById('panel-reviews-list');
    if (!container) return;

    const cards = Array.from(container.querySelectorAll('.review-card'));
    cards.sort((a, b) => {
        switch (sortValue) {
            case 'highest': return (parseFloat(b.dataset.score) || 0) - (parseFloat(a.dataset.score) || 0);
            case 'lowest': return (parseFloat(a.dataset.score) || 0) - (parseFloat(b.dataset.score) || 0);
            case 'helpful': return (parseInt(b.dataset.helpful) || 0) - (parseInt(a.dataset.helpful) || 0);
            default: return (b.dataset.date || '').localeCompare(a.dataset.date || '');
        }
    });
    cards.forEach(card => container.appendChild(card));
}

function clearReviewFilters() {
    const travelerSelect = document.getElementById('reviews-filter-traveler');
    const scoreSelect = document.getElementById('reviews-filter-score');
    if (travelerSelect) travelerSelect.value = '';
    if (scoreSelect) scoreSelect.value = '';
    document.querySelectorAll('.topic-filter').forEach(btn => btn.classList.remove('active'));
    filterReviews();
}

// ============================================
// ROOM DETAIL MODAL
// ============================================

let roomModalPhotoIndex = 0;
let roomModalPhotos = [];

function filterRoomTypes(showRooms, showSuites, showApartments) {
    document.querySelectorAll('tr[data-room-category]').forEach(row => {
        const cat = row.getAttribute('data-room-category');
        if (cat === 'room') row.style.display = showRooms ? '' : 'none';
        else if (cat === 'suite') row.style.display = showSuites ? '' : 'none';
        else if (cat === 'apartment') row.style.display = showApartments ? '' : 'none';
    });
}

function pillIcon(name) {
    const lower = name.toLowerCase();
    if (lower.includes('wifi')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>';
    if (lower.includes('air conditioning') || lower.includes('air-condition')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>';
    if (lower.includes('view')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>';
    if (lower.includes('balcony') || lower.includes('terrace')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>';
    if (lower.includes('bathtub') || lower.includes('spa bath') || lower.includes('hot tub')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2v-5H3v5a2 2 0 002 2z"/>';
    if (lower.includes('kitchen') || lower.includes('kitchenette')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>';
    if (lower.includes('minibar') || lower.includes('mini-bar')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>';
    if (lower.includes('safe')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>';
    if (lower.includes('tv') || lower.includes('screen') || lower.includes('television')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>';
    if (lower.includes('soundproof')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/>';
    if (lower.includes('washing') || lower.includes('laundry')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>';
    if (lower.includes('pool')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15c2.67 0 4-2 6.67-2s4 2 6.66 2 4-2 6.67-2M3 20c2.67 0 4-2 6.67-2s4 2 6.66 2 4-2 6.67-2"/>';
    if (lower.includes('private bathroom')) return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>';
    return '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>';
}

function openRoomModal(groupIndex) {
    const groups = window.roomGroupsData;
    if (!groups || !groups[groupIndex]) return;

    const group = groups[groupIndex];
    const overlay = document.getElementById('room-modal-overlay');
    const modal = document.getElementById('room-modal');
    const content = document.getElementById('room-modal-content');
    if (!overlay || !modal || !content) return;

    roomModalPhotos = group.images || [];
    roomModalPhotoIndex = 0;

    const firstRate = group.rates && group.rates.length > 0 ? group.rates[0] : null;
    const smoking = firstRate ? firstRate.smoking : false;
    const bedrooms = firstRate ? firstRate.bedrooms : 0;
    const bathrooms = firstRate ? firstRate.bathrooms : 0;
    const facilityGroups = group.facility_groups || [];
    const highlightPills = group.highlight_pills || [];

    let html = '<div class="flex flex-col md:flex-row h-full">';

    // LEFT COLUMN: Photo gallery
    html += '<div class="md:w-[55%] flex-shrink-0 bg-gray-100 flex flex-col">';
    if (roomModalPhotos.length > 0) {
        html += `<div class="relative flex-1 min-h-[250px] md:min-h-0">
            <img id="room-modal-photo" src="${roomModalPhotos[0]}" alt="${group.name}" class="w-full h-full object-cover absolute inset-0">`;
        if (roomModalPhotos.length > 1) {
            html += `<button onclick="roomGalleryNav(-1)" class="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <button onclick="roomGalleryNav(1)" class="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div id="room-modal-photo-counter" class="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">1 / ${roomModalPhotos.length}</div>`;
        }
        html += '</div>';
        if (roomModalPhotos.length > 1 && roomModalPhotos.length <= 10) {
            html += '<div id="room-modal-dots" class="flex justify-center gap-1.5 py-1.5 bg-gray-100">';
            roomModalPhotos.forEach((_, i) => { html += `<span class="w-2 h-2 rounded-full transition-colors ${i === 0 ? 'bg-gray-800' : 'bg-gray-400'}" data-dot-index="${i}"></span>`; });
            html += '</div>';
        }
        if (roomModalPhotos.length > 1) {
            html += '<div id="room-modal-thumbs" class="flex gap-1.5 p-2 overflow-x-auto flex-shrink-0 bg-gray-50">';
            roomModalPhotos.forEach((photo, i) => {
                const thumbUrl = photo.replace('/max1280x900/', '/square90/').replace('/max1280/', '/square90/').replace('/max750/', '/square90/');
                html += `<button onclick="roomGalleryJump(${i})" class="flex-shrink-0 rounded overflow-hidden border-2 transition-colors ${i === 0 ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}" data-thumb-index="${i}">
                    <img src="${thumbUrl}" alt="" class="w-14 h-14 object-cover">
                </button>`;
            });
            html += '</div>';
        }
    } else {
        html += `<div class="flex-1 flex items-center justify-center text-gray-400">
            <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
        </div>`;
    }
    html += '</div>';

    // RIGHT COLUMN: Room details
    html += '<div class="md:w-[45%] overflow-y-auto p-5 md:p-6">';
    html += `<h3 class="text-xl font-bold text-gray-900 mb-3">${group.name}</h3>`;

    const attrs = [];
    if (bedrooms > 0) attrs.push(`<span class="inline-flex items-center gap-1.5 text-sm text-gray-900"><svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4"/></svg>${bedrooms} bedroom${bedrooms > 1 ? 's' : ''}</span>`);
    if (bathrooms > 0) attrs.push(`<span class="inline-flex items-center gap-1.5 text-sm text-gray-900"><svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2v-5H3v5a2 2 0 002 2z"/></svg>${bathrooms} bathroom${bathrooms > 1 ? 's' : ''}</span>`);
    if (attrs.length > 0) html += `<div class="flex flex-wrap gap-x-4 gap-y-2 mb-4 pb-4 border-b border-gray-100">${attrs.join('')}</div>`;

    if (group.description) {
        const desc = group.description;
        html += `<div class="mb-4"><p class="text-sm text-gray-900 leading-relaxed">${desc.length > 200 ? desc.substring(0, 200) + '...' : desc}</p></div>`;
    }

    if (group.bed_config && group.bed_config.length > 0) {
        group.bed_config.forEach(bed => {
            html += `<div class="flex items-center gap-2 text-sm text-gray-900 mb-3">
                <svg class="w-5 h-5 text-gray-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 12V8a2 2 0 00-2-2H6a2 2 0 00-2 2v4m16 0v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5m16 0H4"/></svg>
                <span>${bed.type}</span>
            </div>`;
        });
    }

    if (window.reviewComfort > 0 && window.reviewCount > 0) {
        html += `<p class="text-sm text-gray-600 mb-4">Comfy beds, ${window.reviewComfort} \u2013 Based on ${window.reviewCount} reviews</p>`;
    }

    if (group.max_occupancy && (group.max_occupancy.total > 0 || group.max_occupancy.adults > 0)) {
        const occ = group.max_occupancy;
        const pi = (cls) => `<svg class="${cls}" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/></svg>`;
        html += '<div class="flex items-center gap-1 mb-4">';
        if (occ.adults > 0) {
            for (let i = 0; i < occ.adults; i++) html += pi('w-5 h-5 text-gray-900');
            if (occ.children > 0) {
                html += '<span class="text-gray-400 text-xs mx-0.5">+</span>';
                for (let i = 0; i < occ.children; i++) html += pi('w-3.5 h-3.5 text-gray-600');
            }
        } else {
            for (let i = 0; i < occ.total; i++) html += pi('w-5 h-5 text-gray-900');
        }
        html += '</div>';
    }

    if (highlightPills.length > 0 || group.size > 0) {
        html += '<div class="flex flex-wrap gap-2 mb-4">';
        if (group.size > 0) {
            html += `<span class="inline-flex items-center gap-1.5 px-2.5 py-1 border border-gray-300 text-gray-900 text-xs font-medium rounded">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/></svg>
                ${group.size} ${group.size_unit || 'm\u00B2'}
            </span>`;
        }
        highlightPills.forEach(f => {
            html += `<span class="inline-flex items-center gap-1.5 px-2.5 py-1 border border-gray-300 text-gray-900 text-xs font-medium rounded">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">${pillIcon(f)}</svg>
                ${f}
            </span>`;
        });
        html += '</div>';
    }

    if (group.size > 0) html += `<p class="text-sm mb-3"><strong>Room Size</strong> ${group.size} ${group.size_unit || 'm\u00B2'}</p>`;

    // Reserve card
    if (window.hasDates && firstRate && firstRate.price && firstRate.price.amount > 0) {
        const price = firstRate.price;
        const opts = firstRate.options || {};
        const occ = firstRate.max_occupancy || group.max_occupancy || {};
        const sAdults = window.searchAdults || 2;
        const sChildren = window.searchChildren || 0;
        const personSvg = (cls) => `<svg class="${cls}" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/></svg>`;

        let recText = `Recommended for ${sAdults} adult${sAdults !== 1 ? 's' : ''}`;
        if (sChildren > 0) recText += `, ${sChildren} child${sChildren !== 1 ? 'ren' : ''}`;

        html += '<div class="mt-4 pt-4 border-t-2 border-gray-200 -mx-5 md:-mx-6 px-5 md:px-6 pb-4 bg-gray-50">';
        html += `<p class="text-base font-bold text-gray-900 mb-3">${recText}</p>`;
        html += `<p class="text-sm font-semibold text-gray-900 mb-1">1 \u00D7 ${group.name}</p>`;

        html += '<div class="flex items-center gap-1 mb-2 text-sm text-gray-700"><span class="mr-1">Price for:</span>';
        for (let i = 0; i < sAdults; i++) html += personSvg('w-4 h-4 text-gray-700');
        if (sChildren > 0) {
            html += '<span class="text-gray-400 text-xs mx-0.5">+</span>';
            for (let i = 0; i < sChildren; i++) html += personSvg('w-3 h-3 text-gray-500');
        }
        html += '</div>';

        if (group.bed_config && group.bed_config.length > 0) {
            const bedText = group.bed_config.map(b => `${b.count > 1 ? b.count + ' ' : ''}${b.type}`).join(', ');
            html += `<div class="flex items-center gap-2 text-sm text-gray-700 mb-2">
                <svg class="w-4 h-4 text-gray-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 12V8a2 2 0 00-2-2H6a2 2 0 00-2 2v4m16 0v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5m16 0H4"/></svg>
                Bed: ${bedText}
            </div>`;
        }

        if (occ.children > 0) html += `<p class="text-sm text-blue-600 font-medium mb-2">Free stay for your child</p>`;

        if (opts.free_cancellation) {
            const cancelText = opts.cancellation_text || (opts.cancel_before ? 'Cancel without fees before ' + opts.cancel_before : 'Free cancellation');
            html += `<div class="flex items-start gap-1.5 text-sm text-green-700 mb-1"><svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg><span>${cancelText}</span></div>`;
        } else {
            html += `<div class="flex items-start gap-1.5 text-sm text-gray-600 mb-1"><svg class="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/></svg><span>Non-refundable</span></div>`;
        }

        if (opts.no_prepayment) {
            const prepayText = opts.prepayment_text || opts.prepayment || '';
            if (prepayText) {
                html += `<div class="flex items-start gap-1.5 text-sm text-green-700 mb-1"><svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg><span>${prepayText}</span></div>`;
            }
        }

        if (opts.breakfast || opts.breakfast_type || opts.mealplan_text) {
            const mealText = opts.breakfast_type || (opts.mealplan_text && opts.mealplan_text.length < 80 ? opts.mealplan_text : null) || 'Breakfast included';
            const isMealIncluded = opts.breakfast && !opts.meal_optional;
            const mealIcon = isMealIncluded
                ? `<svg class="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`
                : `<svg class="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>`;
            html += `<div class="flex items-start gap-1.5 text-sm ${isMealIncluded ? 'text-green-700' : 'text-gray-600'} mb-1">${mealIcon}<span>${mealText}${opts.meal_optional ? ' (optional)' : ''}</span></div>`;
        }

        let stayCtx = `${window.numNights} night${window.numNights !== 1 ? 's' : ''}, ${sAdults} adult${sAdults !== 1 ? 's' : ''}`;
        if (sChildren > 0) stayCtx += `, ${sChildren} child${sChildren !== 1 ? 'ren' : ''}`;
        html += `<div class="flex items-end justify-between gap-4 mt-3 pt-3 border-t border-gray-200">`;
        // Wrap in <price-display> so the modal price reflects the user's
        // current preferred currency (and re-renders if they switch while
        // the modal is open). Inner SSR text uses Math.floor + 'en-US' to
        // match price-display's _ssrEquivalentText() — otherwise the first
        // hydration on connect would flip the visible text from
        // "€294.50" (fmtAmountDisplay's currency-decimals) to "€294"
        // (_formatAmount's floor-to-integer), since
        // _maybeReformat → _ssrEquivalentText runs eagerly when target===source.
        const _modalSrcAmt = (price.amount || 0).toFixed(2);
        const _modalSsr = price.currency_symbol + Math.floor(price.amount || 0).toLocaleString('en-US');
        html += `<div><p class="text-xs text-gray-500">${stayCtx}</p><p class="text-2xl font-bold text-gray-900"><price-display data-rollout-eligible="true" source-amount="${_modalSrcAmt}" source-currency="${price.currency || ''}">${_modalSsr}</price-display></p><p class="text-xs text-gray-500">Includes taxes and fees</p></div>`;
        html += `<div class="flex-shrink-0"><button onclick="closeRoomModal(); document.querySelector('.room-select')?.scrollIntoView({behavior:'smooth', block:'center'});" class="px-6 py-2.5 bg-booking-blue text-white text-sm font-semibold rounded hover:bg-booking-blue-dark transition-colors whitespace-nowrap">Reserve</button></div>`;
        html += '</div>';
        html += `<p class="text-xs text-gray-400 mt-2 text-center">Don\u2019t worry \u2013 clicking won\u2019t charge you</p>`;
        html += '</div>';
    }

    // Categorized facilities
    if (facilityGroups.length > 0) {
        html += '<div class="border-t border-gray-100 pt-4 grid grid-cols-2 gap-x-4">';
        facilityGroups.forEach(grp => {
            html += `<div class="mb-3 break-inside-avoid"><p class="text-sm font-semibold text-gray-900 mb-1">${grp.name}</p>`;
            grp.facilities.forEach(f => {
                html += `<div class="flex items-center gap-1.5 text-sm text-gray-900 py-0.5"><svg class="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>${f}</div>`;
            });
            html += '</div>';
        });
        html += `<div class="mb-3"><p class="text-sm font-semibold text-gray-900 mb-1">Smoking</p>
            <div class="flex items-center gap-1.5 text-sm text-gray-900 py-0.5">
                <svg class="w-3.5 h-3.5 ${smoking ? 'text-gray-400' : 'text-green-600'} flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${smoking ? 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' : 'M5 13l4 4L19 7'}"/></svg>
                ${smoking ? 'Smoking allowed' : 'No smoking'}
            </div></div>`;
        html += '</div>';
    } else {
        html += `<div class="mt-3 pt-3 border-t border-gray-100"><p class="text-sm"><strong>Smoking:</strong> ${smoking ? 'Smoking allowed' : 'No smoking'}</p></div>`;
    }

    html += '</div></div>';
    content.innerHTML = html;

    overlay.classList.remove('hidden');
    modal.classList.remove('hidden');
    requestAnimationFrame(() => { overlay.classList.add('opacity-100'); overlay.classList.remove('opacity-0'); });
    document.body.style.overflow = 'hidden';
}

function closeRoomModal() {
    const overlay = document.getElementById('room-modal-overlay');
    const modal = document.getElementById('room-modal');
    if (!overlay || !modal) return;
    overlay.classList.add('opacity-0');
    overlay.classList.remove('opacity-100');
    setTimeout(() => { overlay.classList.add('hidden'); modal.classList.add('hidden'); }, 300);
    document.body.style.overflow = '';
}

function roomGalleryNav(delta) {
    if (roomModalPhotos.length === 0) return;
    roomModalPhotoIndex = (roomModalPhotoIndex + delta + roomModalPhotos.length) % roomModalPhotos.length;
    updateRoomGalleryDisplay();
}

function roomGalleryJump(index) {
    if (index < 0 || index >= roomModalPhotos.length) return;
    roomModalPhotoIndex = index;
    updateRoomGalleryDisplay();
}

function updateRoomGalleryDisplay() {
    const img = document.getElementById('room-modal-photo');
    const counter = document.getElementById('room-modal-photo-counter');
    if (img) img.src = roomModalPhotos[roomModalPhotoIndex];
    if (counter) counter.textContent = `${roomModalPhotoIndex + 1} / ${roomModalPhotos.length}`;

    document.querySelectorAll('#room-modal-thumbs button[data-thumb-index]').forEach(btn => {
        const idx = parseInt(btn.getAttribute('data-thumb-index'));
        if (idx === roomModalPhotoIndex) {
            btn.classList.add('border-blue-500');
            btn.classList.remove('border-transparent');
            btn.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        } else {
            btn.classList.remove('border-blue-500');
            btn.classList.add('border-transparent');
        }
    });

    document.querySelectorAll('#room-modal-dots span[data-dot-index]').forEach(dot => {
        const idx = parseInt(dot.getAttribute('data-dot-index'));
        if (idx === roomModalPhotoIndex) { dot.classList.add('bg-gray-800'); dot.classList.remove('bg-gray-400'); }
        else { dot.classList.remove('bg-gray-800'); dot.classList.add('bg-gray-400'); }
    });
}

// Room modal keyboard handlers
document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
        var bookingModal = document.getElementById('booking-modal');
        if (bookingModal && !bookingModal.classList.contains('hidden')) { closeBookingModal(); return; }
    }
    const modal = document.getElementById('room-modal');
    if (!modal || modal.classList.contains('hidden')) return;
    if (e.key === 'Escape') closeRoomModal();
    else if (e.key === 'ArrowLeft') roomGalleryNav(-1);
    else if (e.key === 'ArrowRight') roomGalleryNav(1);
});

// ============================================
// SHARED HELPERS
// ============================================

function escapeHTML(str) {
    if (!str) return '';
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
}

function countryFlag(code) {
    if (!code || code.length !== 2) return '';
    const upper = code.toUpperCase();
    return String.fromCodePoint(0x1F1E6 + upper.charCodeAt(0) - 65)
         + String.fromCodePoint(0x1F1E6 + upper.charCodeAt(1) - 65);
}

function formatCountry(review) {
    const code = review.country_code || '';
    const name = review.country || '';
    const flag = countryFlag(code);
    return flag + (flag ? ' ' : '') + escapeHTML(name || code.toUpperCase());
}

function formatReviewDate(dateStr) {
    try {
        const d = new Date(dateStr);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    } catch { return dateStr; }
}

// ============================================
// INIT
// ============================================

document.addEventListener('DOMContentLoaded', function() {
    initReserveButtonDelegation();
    initMapLazy();
    initGallery();
    // Topic filter buttons
    document.querySelectorAll('.topic-filter').forEach(btn => {
        btn.addEventListener('click', function() {
            this.classList.toggle('active');
            filterReviews();
        });
    });

    // Close reviews panel on Escape
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') closeReviewsPanel();
    });
});

